import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import moment from 'moment';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import * as adminService from '../../services/admin';

const RecurringTransactions = () => {
    const [activeTab, setActiveTab] = useState('list');
    const [transactions, setTransactions] = useState([]);
    const [dueTransactions, setDueTransactions] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(localStorage.getItem('branch_id') || '1');
    const [loading, setLoading] = useState(false);

    // Form state
    const [showModal, setShowModal] = useState(false);
    const [transactionForm, setTransactionForm] = useState({
        transaction_name: '',
        description: '',
        transaction_type: 'expense',
        voucher_type: 'payment',
        amount: '',
        debit_account_id: '',
        credit_account_id: '',
        frequency: 'monthly',
        start_date: moment().format('YYYY-MM-DD'),
        end_date: '',
        day_of_month: 1,
        day_of_week: 'Monday',
        occurrences: '',
        is_active: true,
        auto_post: true
    });
    const [editingId, setEditingId] = useState(null);

    // History modal
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [history, setHistory] = useState([]);
    const [selectedTransactionId, setSelectedTransactionId] = useState(null);

    useEffect(() => {
        if (activeTab === 'list') {
            fetchTransactions();
        } else if (activeTab === 'due') {
            fetchDueTransactions();
        }
        fetchAccounts();
        fetchTemplates();
    }, [activeTab, selectedBranch]);

    const fetchTransactions = async () => {
        try {
            setLoading(true);
            const response = await adminService.getRecurringTransactions({ branch_id: selectedBranch });
            if (response.status === 'success') {
                setTransactions(response.data);
            }
        } catch (error) {
            toast.error('Failed to fetch recurring transactions');
        } finally {
            setLoading(false);
        }
    };

    const fetchDueTransactions = async () => {
        try {
            setLoading(true);
            const response = await adminService.getDueTransactions({ branch_id: selectedBranch });
            if (response.status === 'success') {
                setDueTransactions(response.data);
            }
        } catch (error) {
            toast.error('Failed to fetch due transactions');
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            const response = await adminService.getAccountList({ branch_id: selectedBranch });
            if (response.status === 'success') {
                setAccounts(response.data || response.accounts || []);
            }
        } catch (error) {
            console.error('Failed to fetch accounts');
        }
    };

    const fetchTemplates = async () => {
        try {
            const response = await adminService.getRecurringTemplates();
            if (response.status === 'success') {
                setTemplates(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch templates');
        }
    };

    const handleSaveTransaction = async () => {
        try {
            const data = {
                ...transactionForm,
                branch_id: selectedBranch
            };

            if (editingId) {
                await adminService.updateRecurringTransaction(editingId, data);
                toast.success('Transaction updated successfully');
            } else {
                await adminService.createRecurringTransaction(data);
                toast.success('Transaction created successfully');
            }

            setShowModal(false);
            resetForm();
            fetchTransactions();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save transaction');
        }
    };

    const handleEditTransaction = (transaction) => {
        setTransactionForm({
            transaction_name: transaction.transaction_name,
            description: transaction.description || '',
            transaction_type: transaction.transaction_type,
            voucher_type: transaction.voucher_type,
            amount: transaction.amount,
            debit_account_id: transaction.debit_account_id || '',
            credit_account_id: transaction.credit_account_id || '',
            frequency: transaction.frequency,
            start_date: moment(transaction.start_date).format('YYYY-MM-DD'),
            end_date: transaction.end_date ? moment(transaction.end_date).format('YYYY-MM-DD') : '',
            day_of_month: transaction.day_of_month || 1,
            day_of_week: transaction.day_of_week || 'Monday',
            occurrences: transaction.occurrences || '',
            is_active: transaction.is_active,
            auto_post: transaction.auto_post
        });
        setEditingId(transaction.id);
        setShowModal(true);
    };

    const handleDeleteTransaction = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This will delete the recurring transaction permanently',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await adminService.deleteRecurringTransaction(id);
                toast.success('Transaction deleted successfully');
                fetchTransactions();
            } catch (error) {
                toast.error('Failed to delete transaction');
            }
        }
    };

    const handleExecuteTransaction = async (id) => {
        const result = await Swal.fire({
            title: 'Execute Transaction?',
            text: 'This will generate a voucher for this recurring transaction',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, execute it!'
        });

        if (result.isConfirmed) {
            try {
                await adminService.executeRecurringTransaction(id);
                toast.success('Transaction executed successfully');
                fetchTransactions();
                fetchDueTransactions();
            } catch (error) {
                toast.error('Failed to execute transaction');
            }
        }
    };

    const handlePauseTransaction = async (id) => {
        try {
            await adminService.pauseRecurringTransaction(id);
            toast.success('Transaction paused successfully');
            fetchTransactions();
        } catch (error) {
            toast.error('Failed to pause transaction');
        }
    };

    const handleResumeTransaction = async (id) => {
        try {
            await adminService.resumeRecurringTransaction(id);
            toast.success('Transaction resumed successfully');
            fetchTransactions();
        } catch (error) {
            toast.error('Failed to resume transaction');
        }
    };

    const handleProcessAllDue = async () => {
        const result = await Swal.fire({
            title: 'Process All Due Transactions?',
            text: `This will process ${dueTransactions.length} due transactions`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, process all!'
        });

        if (result.isConfirmed) {
            try {
                const response = await adminService.processDueTransactions({ branch_id: selectedBranch });
                toast.success(response.message);
                fetchTransactions();
                fetchDueTransactions();
            } catch (error) {
                toast.error('Failed to process due transactions');
            }
        }
    };

    const handleViewHistory = async (id) => {
        try {
            setSelectedTransactionId(id);
            const response = await adminService.getTransactionHistory(id);
            if (response.status === 'success') {
                setHistory(response.data);
                setShowHistoryModal(true);
            }
        } catch (error) {
            toast.error('Failed to fetch history');
        }
    };

    const resetForm = () => {
        setTransactionForm({
            transaction_name: '',
            description: '',
            transaction_type: 'expense',
            voucher_type: 'payment',
            amount: '',
            debit_account_id: '',
            credit_account_id: '',
            frequency: 'monthly',
            start_date: moment().format('YYYY-MM-DD'),
            end_date: '',
            day_of_month: 1,
            day_of_week: 'Monday',
            occurrences: '',
            is_active: true,
            auto_post: true
        });
        setEditingId(null);
    };

    const columnDefs = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'transaction_name', headerName: 'Name', width: 200 },
        { field: 'transaction_type', headerName: 'Type', width: 100 },
        { field: 'frequency', headerName: 'Frequency', width: 120 },
        {
            field: 'amount',
            headerName: 'Amount',
            width: 120,
            valueFormatter: (params) => `₹${parseFloat(params.value).toFixed(2)}`
        },
        {
            field: 'next_run_at',
            headerName: 'Next Run',
            width: 150,
            valueFormatter: (params) => params.value ? moment(params.value).format('YYYY-MM-DD HH:mm') : 'N/A'
        },
        { field: 'completed_occurrences', headerName: 'Completed', width: 120 },
        {
            field: 'is_active',
            headerName: 'Status',
            width: 100,
            cellRenderer: (params) => (
                <span className={`badge ${params.value ? 'bg-success' : 'bg-secondary'}`}>
                    {params.value ? 'Active' : 'Paused'}
                </span>
            )
        },
        {
            headerName: 'Actions',
            width: 350,
            cellRenderer: (params) => (
                <div>
                    <button
                        className="btn btn-sm btn-success me-1"
                        onClick={() => handleExecuteTransaction(params.data.id)}
                        title="Execute Now"
                    >
                        Execute
                    </button>
                    <button
                        className="btn btn-sm btn-info me-1"
                        onClick={() => handleViewHistory(params.data.id)}
                        title="View History"
                    >
                        History
                    </button>
                    <button
                        className="btn btn-sm btn-primary me-1"
                        onClick={() => handleEditTransaction(params.data)}
                    >
                        Edit
                    </button>
                    {params.data.is_active ? (
                        <button
                            className="btn btn-sm btn-warning me-1"
                            onClick={() => handlePauseTransaction(params.data.id)}
                        >
                            Pause
                        </button>
                    ) : (
                        <button
                            className="btn btn-sm btn-success me-1"
                            onClick={() => handleResumeTransaction(params.data.id)}
                        >
                            Resume
                        </button>
                    )}
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteTransaction(params.data.id)}
                    >
                        Delete
                    </button>
                </div>
            )
        }
    ];

    const dueColumnDefs = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'transaction_name', headerName: 'Name', width: 250 },
        { field: 'transaction_type', headerName: 'Type', width: 120 },
        {
            field: 'amount',
            headerName: 'Amount',
            width: 150,
            valueFormatter: (params) => `₹${parseFloat(params.value).toFixed(2)}`
        },
        {
            field: 'next_run_at',
            headerName: 'Due Date',
            width: 180,
            valueFormatter: (params) => moment(params.value).format('YYYY-MM-DD HH:mm')
        },
        {
            headerName: 'Actions',
            width: 150,
            cellRenderer: (params) => (
                <button
                    className="btn btn-sm btn-success"
                    onClick={() => handleExecuteTransaction(params.data.id)}
                >
                    Execute Now
                </button>
            )
        }
    ];

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4>Recurring Transactions</h4>
                        </div>
                        <div className="card-body">
                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'list' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('list')}
                                    >
                                        All Transactions
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'due' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('due')}
                                    >
                                        Due Transactions <span className="badge bg-danger">{dueTransactions.length}</span>
                                    </button>
                                </li>
                            </ul>

                            {activeTab === 'list' && (
                                <div>
                                    <button
                                        className="btn btn-primary mb-3"
                                        onClick={() => {
                                            resetForm();
                                            setShowModal(true);
                                        }}
                                    >
                                        Create Recurring Transaction
                                    </button>

                                    <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
                                        <AgGridReact
                                            rowData={transactions}
                                            columnDefs={columnDefs}
                                            pagination={true}
                                            paginationPageSize={20}
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === 'due' && (
                                <div>
                                    {dueTransactions.length > 0 && (
                                        <button
                                            className="btn btn-success mb-3"
                                            onClick={handleProcessAllDue}
                                        >
                                            Process All Due Transactions
                                        </button>
                                    )}

                                    {dueTransactions.length === 0 ? (
                                        <div className="alert alert-info">
                                            No transactions are currently due for processing.
                                        </div>
                                    ) : (
                                        <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
                                            <AgGridReact
                                                rowData={dueTransactions}
                                                columnDefs={dueColumnDefs}
                                                pagination={true}
                                                paginationPageSize={20}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingId ? 'Edit Recurring Transaction' : 'Create Recurring Transaction'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Transaction Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={transactionForm.transaction_name}
                                            onChange={(e) => setTransactionForm({...transactionForm, transaction_name: e.target.value})}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Amount *</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={transactionForm.amount}
                                            onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
                                        />
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Transaction Type *</label>
                                        <select
                                            className="form-control"
                                            value={transactionForm.transaction_type}
                                            onChange={(e) => setTransactionForm({...transactionForm, transaction_type: e.target.value})}
                                        >
                                            <option value="income">Income</option>
                                            <option value="expense">Expense</option>
                                            <option value="transfer">Transfer</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Voucher Type *</label>
                                        <select
                                            className="form-control"
                                            value={transactionForm.voucher_type}
                                            onChange={(e) => setTransactionForm({...transactionForm, voucher_type: e.target.value})}
                                        >
                                            <option value="receipt">Receipt</option>
                                            <option value="payment">Payment</option>
                                            <option value="journal">Journal</option>
                                            <option value="contra">Contra</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Debit Account</label>
                                        <select
                                            className="form-control"
                                            value={transactionForm.debit_account_id}
                                            onChange={(e) => setTransactionForm({...transactionForm, debit_account_id: e.target.value})}
                                        >
                                            <option value="">Select Account</option>
                                            {accounts.map(account => (
                                                <option key={account.id} value={account.id}>
                                                    {account.account_name} ({account.account_code})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Credit Account</label>
                                        <select
                                            className="form-control"
                                            value={transactionForm.credit_account_id}
                                            onChange={(e) => setTransactionForm({...transactionForm, credit_account_id: e.target.value})}
                                        >
                                            <option value="">Select Account</option>
                                            {accounts.map(account => (
                                                <option key={account.id} value={account.id}>
                                                    {account.account_name} ({account.account_code})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Frequency *</label>
                                        <select
                                            className="form-control"
                                            value={transactionForm.frequency}
                                            onChange={(e) => setTransactionForm({...transactionForm, frequency: e.target.value})}
                                        >
                                            <option value="daily">Daily</option>
                                            <option value="weekly">Weekly</option>
                                            <option value="biweekly">Bi-weekly</option>
                                            <option value="monthly">Monthly</option>
                                            <option value="quarterly">Quarterly</option>
                                            <option value="half_yearly">Half-yearly</option>
                                            <option value="yearly">Yearly</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Start Date *</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={transactionForm.start_date}
                                            onChange={(e) => setTransactionForm({...transactionForm, start_date: e.target.value})}
                                        />
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">End Date (Optional)</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={transactionForm.end_date}
                                            onChange={(e) => setTransactionForm({...transactionForm, end_date: e.target.value})}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Max Occurrences (Optional)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={transactionForm.occurrences}
                                            onChange={(e) => setTransactionForm({...transactionForm, occurrences: e.target.value})}
                                            placeholder="Leave blank for unlimited"
                                        />
                                    </div>
                                </div>

                                {transactionForm.frequency === 'monthly' && (
                                    <div className="mb-3">
                                        <label className="form-label">Day of Month</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            min="1"
                                            max="31"
                                            value={transactionForm.day_of_month}
                                            onChange={(e) => setTransactionForm({...transactionForm, day_of_month: e.target.value})}
                                        />
                                    </div>
                                )}

                                {transactionForm.frequency === 'weekly' && (
                                    <div className="mb-3">
                                        <label className="form-label">Day of Week</label>
                                        <select
                                            className="form-control"
                                            value={transactionForm.day_of_week}
                                            onChange={(e) => setTransactionForm({...transactionForm, day_of_week: e.target.value})}
                                        >
                                            <option value="Monday">Monday</option>
                                            <option value="Tuesday">Tuesday</option>
                                            <option value="Wednesday">Wednesday</option>
                                            <option value="Thursday">Thursday</option>
                                            <option value="Friday">Friday</option>
                                            <option value="Saturday">Saturday</option>
                                            <option value="Sunday">Sunday</option>
                                        </select>
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={transactionForm.description}
                                        onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
                                    ></textarea>
                                </div>

                                <div className="form-check mb-2">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={transactionForm.auto_post}
                                        onChange={(e) => setTransactionForm({...transactionForm, auto_post: e.target.checked})}
                                    />
                                    <label className="form-check-label">
                                        Auto-post generated vouchers
                                    </label>
                                </div>

                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={transactionForm.is_active}
                                        onChange={(e) => setTransactionForm({...transactionForm, is_active: e.target.checked})}
                                    />
                                    <label className="form-check-label">
                                        Active
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSaveTransaction}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* History Modal */}
            {showHistoryModal && (
                <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Transaction History</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowHistoryModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Status</th>
                                            <th>Voucher ID</th>
                                            <th>Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((item) => (
                                            <tr key={item.id}>
                                                <td>{moment(item.execution_date).format('YYYY-MM-DD HH:mm')}</td>
                                                <td>
                                                    <span className={`badge ${
                                                        item.status === 'success' ? 'bg-success' :
                                                        item.status === 'failed' ? 'bg-danger' : 'bg-warning'
                                                    }`}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td>{item.voucher_id || 'N/A'}</td>
                                                <td>{item.details || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {history.length === 0 && (
                                    <div className="alert alert-info">
                                        No execution history found.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecurringTransactions;
